import React from "react";
import { TEXT_LEVELS, Text, plainText, safeElement, type TextLevel } from "../../atoms/text/text";
import { isRenderable } from "./is-renderable.shared";
import { LogoFallback } from "./logo-fallback.client";
import { ScrollToTop } from "./scroll-to-top.client";
import { CHANNEL_LINEUP_TOP_ANCHOR_ID } from "./scroll-to-top.shared";
import styles from "./channel-lineup.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** The view a caller has currently selected (plan §1.2 — URL-driven state
 *  owned by the organism's caller, never read here). Only `"genre"` changes
 *  what this root draws (PG3's genre-only row). */
export type ChannelLineupView = "signature" | "genre";

export interface ChannelLineupProps {
  /** PG1. Absent, no heading is drawn and the region goes unnamed. */
  heading_content?: string;
  /** PG1 — "the page's main heading unless the content team sets a different
   *  heading level". Defaults to `h1`. */
  heading_level?: TextLevel;
  /** PG6 — shown when `selectedViewSlot` would draw nothing. Absent, the error box is empty. */
  error_message_content?: string;
  /** PG4 — the back-to-top control's label. Absent, the control is not drawn. */
  back_to_top_label?: string;

  /** PG3 — which view is active. Drives only the genre-only row below. */
  view: ChannelLineupView;
  /** VT1. Rendered where the view toggle goes; this root neither builds nor
   *  reads it. */
  viewToggleSlot: React.ReactNode;
  /** ZP line. The organism never fetches locals or owns market state — see
   *  plan §1.4. */
  zipSlot: React.ReactNode;
  /** SR8 (Reset Search) plus the search field, as one unit in the PG3 order. */
  searchSlot: React.ReactNode;
  /** PG3, Genre Packs only — VT5's subheading, under the view toggle row. */
  genreSubheadingSlot?: React.ReactNode;
  /** PG3, Genre Packs only — the legal note. */
  genreLegalSlot?: React.ReactNode;
  /**
   * The active view's own content (the Signature grid or the Genre Pack list) — built elsewhere and
   * handed in whole; this root places it last.
   */
  selectedViewSlot: React.ReactNode;
  /**
   * FT1/FT2 — the PDF link and the satellite button, placed below the selected view. Built
   * elsewhere (`lineup-footer.tsx`); this root only positions it.
   */
  footerSlot?: React.ReactNode;
}

/* PG1 names the region — without it the root is a landmark with no label, which the a11y tree
   reports as a plain generic. */
export const CHANNEL_LINEUP_HEADING_ID = "channel-lineup-heading";

/**
 * Channel Lineup — the organism root (PG1-PG6). Server Component: it owns the heading, the PG3
 * order and the PG6 error swap, and nothing else.
 */
function ChannelLineupImpl({
  heading_content: headingContent,
  heading_level: headingLevel,
  error_message_content: errorMessageContent,
  back_to_top_label: backToTopLabel,
  view,
  viewToggleSlot,
  zipSlot,
  searchSlot,
  genreSubheadingSlot,
  genreLegalSlot,
  selectedViewSlot,
  footerSlot
}: ChannelLineupProps) {
  const heading = plainText((headingContent ?? "").trim());
  const level = safeElement(headingLevel, "h1", TEXT_LEVELS);
  const isGenreView = view === "genre";
  /* Every value React draws nothing for, not `null` alone — see the prop. */
  const unbuildable = !isRenderable(selectedViewSlot);
  const errorMessage = plainText((errorMessageContent ?? "").trim());
  const backToTop = plainText((backToTopLabel ?? "").trim());

  return (
    <section
      aria-labelledby={heading !== "" ? CHANNEL_LINEUP_HEADING_ID : undefined}
      className={styles.root}
    >
      {/* PG4's sentinel — zero-size, hidden from assistive tech. ScrollToTop
          observes it to learn whether the reader has moved away from the
          top; it carries no content of its own. */}
      <span aria-hidden="true" className={styles.topAnchor} id={CHANNEL_LINEUP_TOP_ANCHOR_ID} />
      <LogoFallback />
      <div className={styles.content}>
        {heading !== "" && (
          <Text
            as={level}
            className={styles.heading}
            id={CHANNEL_LINEUP_HEADING_ID}
            variant="headline-1"
          >
            {heading}
          </Text>
        )}

        <div className={styles.viewToggle}>{viewToggleSlot}</div>
        <div className={styles.zipLine}>{zipSlot}</div>
        <div className={styles.searchRow}>{searchSlot}</div>

        {/* `isRenderable` per slot rather than `Boolean(a || b)`: the operands are ReactNodes, so a caller's `{items.length && <Sub/>}` makes a slot `0` and a bare `a || b` yields its last operand when none is truthy — an empty slot would land as the text "0" out here in `.content`. */}
        {isGenreView && (isRenderable(genreSubheadingSlot) || isRenderable(genreLegalSlot)) && (
          <div className={styles.genreIntro}>
            {genreSubheadingSlot}
            {genreLegalSlot}
          </div>
        )}

        {unbuildable ? (
          <div className={styles.error} role="alert">
            <Text as="p" variant="body-2">
              {errorMessage}
            </Text>
          </div>
        ) : (
          <div className={styles.selectedView}>{selectedViewSlot}</div>
        )}

        {footerSlot}
      </div>

      {backToTop !== "" && <ScrollToTop label={backToTop} />}
    </section>
  );
}
export const ChannelLineup = withSafeRender(ChannelLineupImpl, "ChannelLineup");

export default ChannelLineup;
